/*!
 * KHIDYR SMOKE FX — kh-smoke.js (production)
 * Monochrome volumetric-smoke GLSL statement scene with typography embedded
 * in the fog. Strict greyscale, #0d0d0d floor. Zero dependencies, zero network.
 *
 * Single global: window.initKhidyrSmoke(container, options)
 *
 *   options: {
 *     kicker:       'KHIDYR',                  // small tracked label above the title
 *     title:        'BUILT DIFFERENT',         // drawn in-shader, swallowed by the fog
 *     subline:      'NO NOISE. NO PRESSURE.',  // small tracked line below the title
 *     grainAmount:  0.03,   // shader grain amplitude — kept LOW: the site already
 *                           // runs a site-wide 5% CSS grain overlay
 *     cycleSeconds: 22,     // clarity breath period (type resolves <-> swallowed)
 *     introSeconds: 2.5,    // intro ramp; starts on FIRST visibility, not at init
 *     quality:      'auto', // 'auto' | 'high' | 'low'
 *     maxDPR:       2,
 *     interactive:  true
 *   }
 *
 *   returns handle: {
 *     canvas,                    // the live <canvas> (null in fallback mode)
 *     live,                      // true when the GL scene is running
 *     burst(nx, ny, strength),   // shockwave; nx/ny normalized 0..1 (default center),
 *                                // strength 0..1 (default 1)
 *     pause(), resume(),
 *     destroy()                  // full teardown: every listener/observer/rAF removed
 *   }
 *
 * Emits CustomEvent 'khsmoke:clarity' on the container (throttled ~6/s) with
 * detail.clarity (0..1, smoothed) so DOM UI can sync to the fog cycle. In
 * reduced-motion and fallback modes a single detail.clarity = 1 event fires so
 * clarity-synced UI always shows.
 *
 * Re-initialising on a container that already holds a live instance destroys
 * the old instance first (WeakMap registry) — idempotent for the Shopify
 * theme editor (shopify:section:load / :unload).
 */
(function () {
  'use strict';

  var VERT_SRC =
    'attribute vec2 aPos;\n' +
    'void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }';

  var FRAG_SRC = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    '',
    'uniform vec2  uRes;',
    'uniform float uTime;',
    'uniform float uAspect;',
    'uniform sampler2D uText;',
    'uniform float uIntro;',
    'uniform float uOctaves;',
    'uniform vec2  uPointer;',
    'uniform vec2  uVel;',
    'uniform float uEnergy;',
    'uniform vec2  uBurstPos;',
    'uniform float uBurstAge;',
    'uniform float uGrain;',
    'uniform float uCycle;',
    'uniform float uGrainAmt;',
    '',
    'float hash12(vec2 p){',
    '  vec3 p3 = fract(vec3(p.xyx) * 0.1031);',
    '  p3 += dot(p3, p3.yzx + 33.33);',
    '  return fract((p3.x + p3.y) * p3.z);',
    '}',
    '',
    'float vnoise(vec2 p){',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  float a = hash12(i);',
    '  float b = hash12(i + vec2(1.0, 0.0));',
    '  float c = hash12(i + vec2(0.0, 1.0));',
    '  float d = hash12(i + vec2(1.0, 1.0));',
    '  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);',
    '}',
    '',
    '/* normalized fBm: amplitude-compensated so octave count changes',
    '   (adaptive quality) do not shift overall brightness */',
    'float fbm(vec2 p){',
    '  float v = 0.0;',
    '  float a = 0.5;',
    '  float n = 0.0;',
    '  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);',
    '  for (int i = 0; i < 6; i++){',
    '    if (float(i) >= uOctaves) break;',
    '    v += a * vnoise(p);',
    '    n += a;',
    '    p = m * p + vec2(3.7, 9.1);',
    '    a *= 0.52;',
    '  }',
    '  return v / max(n, 0.0001);',
    '}',
    '',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / uRes;',
    '  vec2 sp = vec2((uv.x - 0.5) * uAspect, uv.y - 0.5);',
    '  float t = uTime;',
    '',
    '  float introExp  = smoothstep(0.0, 0.55, uIntro);',
    '  float introText = smoothstep(0.35, 1.0, uIntro);',
    '',
    '  /* pointer stir field */',
    '  vec2 pd = sp - uPointer;',
    '  float pInf = exp(-dot(pd, pd) * 22.0) * uEnergy;',
    '',
    '  /* tap / click / CTA shockwave */',
    '  vec2 bd = sp - uBurstPos;',
    '  float bDist = length(bd);',
    '  vec2 bDir = bd / max(bDist, 0.0001);',
    '  float rr = (bDist - uBurstAge * 0.42) * 7.0;',
    '  float ring = exp(-rr * rr) * exp(-uBurstAge * 1.35);',
    '',
    '  /* smoke domain: slow rise + pointer advection + shock push */',
    '  vec2 dom = sp * 2.6;',
    '  dom += vec2(0.0, -t * 0.10);',
    '  dom += uPointer * 0.05;',
    '  dom += uVel * (pInf * 1.3);',
    '  dom += bDir * (ring * 1.1);',
    '',
    '  /* double domain warp (curl-like organic motion) */',
    '  vec2 q = vec2(',
    '    fbm(dom + vec2( t * 0.090, -t * 0.050)),',
    '    fbm(dom + vec2(5.2, 1.3) + vec2(-t * 0.070,  t * 0.040))',
    '  );',
    '  vec2 r = vec2(',
    '    fbm(dom + 1.9 * q + vec2(1.7, 9.2) + vec2( t * 0.120, -t * 0.060)),',
    '    fbm(dom + 1.9 * q + vec2(8.3, 2.8) + vec2(-t * 0.100, -t * 0.050))',
    '  );',
    '  float f = fbm(dom + 2.1 * r);',
    '',
    '  /* silver rim: density gradient toward the light */',
    '  vec2 ldir = normalize(vec2(-0.32, -1.0));',
    '  float f2 = fbm(dom + 2.1 * r - ldir * 0.35);',
    '  float rim = clamp((f - f2) * 2.8, 0.0, 1.0);',
    '',
    '  /* directional shaft from the upper third */',
    '  vec2 so = vec2(uAspect * 0.16, 0.62);',
    '  vec2 sd = sp - so;',
    '  float along = dot(sd, ldir);',
    '  vec2 perp = sd - ldir * along;',
    '  float pdist = length(perp);',
    '  float shaft = exp(-pdist * pdist * 14.0)',
    '              * smoothstep(-0.1, 0.5, along)',
    '              * exp(-max(along, 0.0) * 1.05);',
    '  shaft *= 0.55 + 0.45 * vnoise(vec2(along * 2.5 - t * 0.07, pdist * 7.0));',
    '',
    '  float dens  = smoothstep(0.16, 0.94, f);',
    '  float patch = 0.40 + 0.60 * smoothstep(0.22, 0.78, q.x); /* pools of void */',
    '',
    '  float l = 0.0;',
    '  l += dens * 0.34 * patch;',
    '  l += rim * dens * 0.40;',
    '  l += shaft * (0.22 + 0.78 * dens) * 0.55;',
    '  l *= 0.74 + 0.26 * smoothstep(-0.55, 0.85, sp.y);',
    '  l += pInf * dens * 0.20;',
    '  l += ring * dens * 0.30;',
    '',
    '  /* typography embedded in the fog: displaced, eroded, occluded.',
    '     clarity breathes — type resolves, then the smoke swallows it */',
    '  float cyc  = 0.5 + 0.5 * sin(t * uCycle - 1.2);',
    '  float clar = introText * mix(0.30, 1.0, cyc);',
    '  vec2 tuv = uv;',
    '  tuv += (r - vec2(0.5)) * (0.045 * (1.0 - 0.62 * clar));',
    '  tuv += bDir * (ring * 0.035);',
    '  tuv += uVel * (pInf * 0.02);',
    '  float ta = texture2D(uText, tuv).r;',
    '  float er = mix(1.05, 0.30, clar);',
    '  float th = mix(0.74, 0.28, clar);',
    '  float typ = smoothstep(th, th + 0.42, ta + (r.x - 0.5) * er);',
    '  typ *= 1.0 - 0.72 * smoothstep(0.60, 0.95, f);',
    '  typ *= introText;',
    '  l += typ * (0.94 - l);',
    '',
    '  /* filmic grade: flicker, contrast curve, vignette, grain, lift to #0d0d0d */',
    '  l *= 0.97 + 0.06 * vnoise(vec2(t * 1.7, 3.1));',
    '  l *= introExp;',
    '  l = clamp(l, 0.0, 1.0);',
    '  l = l * l * (3.0 - 2.0 * l);',
    '  l = pow(l, 1.05);',
    '',
    '  float vig = smoothstep(1.08, 0.30, length(sp * vec2(0.80, 1.06)));',
    '  l *= 0.30 + 0.70 * vig;',
    '',
    '  float g = hash12(mod(gl_FragCoord.xy, 512.0) + vec2(uGrain * 113.1, uGrain * 57.7));',
    '  l += (g - 0.5) * uGrainAmt * (0.4 + 0.6 * (1.0 - l));',
    '',
    '  l = clamp(l, 0.0, 1.0);',
    '  l = 0.05 + l * 0.95;',
    '  gl_FragColor = vec4(vec3(l), 1.0);',
    '}'
  ].join('\n');

  /* adaptive quality ladder: render scale (x device DPR) + fbm octaves */
  var LEVELS = [
    { res: 1.00, oct: 5 },
    { res: 0.85, oct: 5 },
    { res: 0.70, oct: 4 },
    { res: 0.55, oct: 4 },
    { res: 0.45, oct: 3 }
  ];

  var MAX_RENDER_PIXELS = 2400000;
  var DROP_FPS = 47;            /* sustained below -> step quality down       */
  var RAISE_FPS = 57;           /* sustained above -> step quality back up    */
  var RAISE_COOLDOWN = 10000;   /* ms since last level change before raising  */
  var OSC_WINDOW = 12000;       /* raise->drop inside this window bars a tier */
  var STIR_K = 0.55;            /* pointer speed -> stir gain                 */
  var STIR_CAP = 1.0;           /* stir ceiling                               */
  var CLARITY_MS = 160;         /* clarity event throttle (~6/s)              */

  var INSTANCES = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function sstep(e0, e1, x) {
    var t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  }

  function initKhidyrSmoke(container, options) {
    if (!container || container.nodeType !== 1) {
      throw new Error('initKhidyrSmoke: container must be a DOM element');
    }

    /* WeakMap registry: re-init on a live container destroys the old instance */
    if (INSTANCES && INSTANCES.has(container)) {
      try { INSTANCES.get(container).destroy(); } catch (e) {}
      INSTANCES['delete'](container);
    }

    var opts = {
      kicker: 'KHIDYR',
      title: 'BUILT DIFFERENT',
      subline: 'NO NOISE. NO PRESSURE.',
      grainAmount: 0.03,
      cycleSeconds: 22,
      introSeconds: 2.5,
      quality: 'auto',
      maxDPR: 2,
      interactive: true
    };
    if (options) {
      for (var k in options) {
        if (Object.prototype.hasOwnProperty.call(options, k)) opts[k] = options[k];
      }
      /* legacy aliases from the lab build */
      if (options.label != null && options.kicker == null) opts.kicker = options.label;
      if (options.sub != null && options.subline == null) opts.subline = options.sub;
    }
    opts.kicker = opts.kicker == null ? '' : String(opts.kicker);
    opts.title = opts.title == null ? '' : String(opts.title);
    opts.subline = opts.subline == null ? '' : String(opts.subline);

    var grainAmt = +opts.grainAmount;
    if (!isFinite(grainAmt)) grainAmt = 0.03;
    grainAmt = clamp(grainAmt, 0, 0.25);
    var cycleSec = +opts.cycleSeconds;
    if (!isFinite(cycleSec)) cycleSec = 22;
    cycleSec = clamp(cycleSec, 4, 120);
    var cycleRate = (Math.PI * 2) / cycleSec;
    var introSec = +opts.introSeconds;
    if (!isFinite(introSec)) introSec = 2.5;
    introSec = clamp(introSec, 0.4, 10);
    /* sim time of the composed static frame = clarity peak of the cycle */
    var staticT = (Math.PI / 2 + 1.2) / cycleRate;

    var FONT = "'Instrument Sans','Helvetica Neue',Helvetica,Arial,sans-serif";

    /* ---------- container + fallback (built safely, no innerHTML) ---------- */
    var cs = window.getComputedStyle(container);
    if (cs.position === 'static') container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.style.background = '#0d0d0d';

    function mkText(tag, text, css) {
      var n = document.createElement(tag);
      n.textContent = text;
      n.style.cssText = css;
      return n;
    }

    var fallback = document.createElement('div');
    fallback.setAttribute('aria-hidden', 'true');
    fallback.style.cssText =
      'position:absolute;inset:0;display:none;flex-direction:column;align-items:center;' +
      'justify-content:center;text-align:center;z-index:1;padding:0 24px;box-sizing:border-box;' +
      'background:radial-gradient(120% 90% at 50% 38%,#1c1c1c 0%,#0d0d0d 55%,#000 100%);' +
      'font-family:' + FONT + ';color:#fff;';
    if (opts.kicker) {
      fallback.appendChild(mkText('div', opts.kicker,
        'font-size:11px;font-weight:600;letter-spacing:.55em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:18px;'));
    }
    fallback.appendChild(mkText('div', opts.title,
      'font-size:clamp(34px,7vw,96px);font-weight:800;letter-spacing:-.02em;text-transform:uppercase;line-height:1.02;'));
    if (opts.subline) {
      fallback.appendChild(mkText('div', opts.subline,
        'font-size:10px;font-weight:600;letter-spacing:.42em;text-transform:uppercase;color:rgba(255,255,255,.45);margin-top:20px;'));
    }
    container.appendChild(fallback);

    function showFallback() { fallback.style.display = 'flex'; if (canvas && canvas.parentNode) canvas.style.opacity = '0'; }
    function hideFallback() { fallback.style.display = 'none'; if (canvas && canvas.parentNode) canvas.style.opacity = '1'; }

    function fireClarity(v) {
      try {
        container.dispatchEvent(new CustomEvent('khsmoke:clarity', { detail: { clarity: v } }));
      } catch (e) {}
    }

    /* ---------- canvas + GL ---------- */
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    container.appendChild(canvas);

    var gl = null;
    try {
      var glAttrs = { alpha: false, depth: false, stencil: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'high-performance' };
      gl = canvas.getContext('webgl', glAttrs) || canvas.getContext('experimental-webgl', glAttrs);
    } catch (e) { gl = null; }

    /* fallback handle: NOTHING was attached yet, so nothing can leak.
       Fires clarity=1 once so clarity-synced UI always shows. */
    function makeStub() {
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      fallback.style.display = 'flex';
      window.setTimeout(function () { fireClarity(1); }, 0);
      var stub = {
        canvas: null,
        live: false,
        burst: function () {},
        pause: function () {},
        resume: function () {},
        destroy: function () {
          if (fallback.parentNode) fallback.parentNode.removeChild(fallback);
          if (INSTANCES && INSTANCES.get(container) === stub) INSTANCES['delete'](container);
        }
      };
      if (INSTANCES) INSTANCES.set(container, stub);
      return stub;
    }

    if (!gl) return makeStub();

    /* ---------- state ---------- */
    var destroyed = false, lost = false, glOK = false;
    var running = false, rafId = 0;
    var userPaused = false;
    var pageVisible = !document.hidden;
    var inView = true;
    var hasIO = ('IntersectionObserver' in window);
    var introStarted = !hasIO;      /* intro starts on first visibility (IO) */
    var reduced = false;
    var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (mq) reduced = !!mq.matches;

    var cssW = Math.max(1, container.clientWidth);
    var cssH = Math.max(1, container.clientHeight);
    var aspect = cssW / cssH;

    /* quality: start one tier lower on small / low-memory devices */
    var startLevel = opts.quality === 'high' ? 0 : (opts.quality === 'low' ? 2 : 0);
    var lowTier = (window.innerWidth < 768) ||
      (typeof navigator !== 'undefined' && navigator.deviceMemory !== undefined && navigator.deviceMemory < 4);
    if (lowTier) startLevel = Math.min(startLevel + 1, LEVELS.length - 1);
    var level = startLevel;
    var raiseCeil = startLevel;     /* anti-oscillation: best tier we may raise back to */
    var lastChange = 0;
    var lastRaise = -1e9;

    var simTime = 0;
    var introT = 0;
    var lastNow = 0;
    var fpsAcc = 0, fpsN = 0;
    var lastClarityAt = -1e9;

    var ptr  = { x: 0, y: 0.05 };   /* smoothed pointer (aspect space) */
    var ptrT = { x: 0, y: 0.05 };   /* pointer target */
    var vel  = { x: 0, y: 0 };      /* smoothed velocity */
    var velT = { x: 0, y: 0 };
    var stirT = 0;                  /* velocity-scaled stir target (decays)  */
    var energy = 0;                 /* smoothed stir fed to the shader       */
    var lastMoveT = 0;
    var bPos = { x: 0, y: 0 };
    var burstAge = 100;             /* large = no visible ring */

    var prog = null, buf = null, textTex = null, U = {};
    var textTimer = 0;
    var disposers = [];

    /* ---------- shaders / GL setup (also used on context restore) ---------- */
    function compile(type, src) {
      var sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
        gl.deleteShader(sh);
        return null;
      }
      return sh;
    }

    function setupGL() {
      glOK = false;
      var vs = compile(gl.VERTEX_SHADER, VERT_SRC);
      var fs = compile(gl.FRAGMENT_SHADER, FRAG_SRC);
      if (!vs || !fs) return false;
      prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS) && !gl.isContextLost()) return false;
      gl.useProgram(prog);

      buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var aPos = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      ['uRes', 'uTime', 'uAspect', 'uText', 'uIntro', 'uOctaves', 'uPointer', 'uVel',
       'uEnergy', 'uBurstPos', 'uBurstAge', 'uGrain', 'uCycle', 'uGrainAmt'].forEach(function (n) {
        U[n] = gl.getUniformLocation(prog, n);
      });

      textTex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform1i(U.uText, 0);
      gl.uniform1f(U.uCycle, cycleRate);
      gl.uniform1f(U.uGrainAmt, grainAmt);

      glOK = true;
      return true;
    }

    /* ---------- typography texture (procedural, no webfont fetch) ---------- */
    function fillTracked(g, text, cx, y, trackPx) {
      var total = 0, i;
      var widths = [];
      for (i = 0; i < text.length; i++) {
        widths[i] = g.measureText(text.charAt(i)).width;
        total += widths[i];
      }
      total += trackPx * Math.max(0, text.length - 1);
      var x = cx - total / 2;
      for (i = 0; i < text.length; i++) {
        g.fillText(text.charAt(i), x, y);
        x += widths[i] + trackPx;
      }
      return total;
    }

    function measureTracked(g, text, fs, trackEm, weight) {
      g.font = weight + ' ' + fs.toFixed(2) + 'px ' + FONT;
      var total = 0;
      for (var i = 0; i < text.length; i++) total += g.measureText(text.charAt(i)).width;
      return total + trackEm * fs * Math.max(0, text.length - 1);
    }

    function buildTextCanvas(w, h) {
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var g = c.getContext('2d');
      g.fillStyle = '#000';
      g.fillRect(0, 0, w, h);
      g.textBaseline = 'alphabetic';
      g.textAlign = 'left';

      var portrait = (w / h) < 0.95;
      var lines = portrait ? opts.title.split(/\s+/) : [opts.title];
      if (!lines.length) lines = [''];

      var widest = 1;
      for (var i = 0; i < lines.length; i++) {
        widest = Math.max(widest, measureTracked(g, lines[i], 100, -0.025, '800'));
      }
      var fs = 100 * (w * (portrait ? 0.86 : 0.84)) / widest;
      fs = Math.min(fs, (h * 0.46) / (lines.length * 1.06));

      var labelFs = Math.max(13, fs * 0.13);
      if (opts.kicker) {
        var lw0 = measureTracked(g, opts.kicker, labelFs, 0.55, '700');
        if (lw0 > w * 0.8) { labelFs *= (w * 0.8) / lw0; }
      }

      var subFs = 0, sw0 = 0;
      if (opts.subline) {
        subFs = Math.max(11, fs * 0.10);
        sw0 = measureTracked(g, opts.subline, subFs, 0.42, '600');
        if (sw0 > w * 0.86) { subFs *= (w * 0.86) / sw0; }
      }

      var lineH = fs * 1.06;
      var blockH = lineH * (lines.length - 1) + fs;
      var firstBase = h * 0.5 - blockH * 0.5 + fs * 0.80;
      var labelY = firstBase - fs * 1.12;
      var subY = firstBase + lineH * (lines.length - 1) + fs * 0.40 + subFs;

      function drawAll() {
        var j;
        g.fillStyle = '#fff';
        /* small kicker label + flanking rules */
        if (opts.kicker) {
          g.font = '700 ' + labelFs.toFixed(2) + 'px ' + FONT;
          var lw = fillTracked(g, opts.kicker, w / 2, labelY, 0.55 * labelFs);
          var ruleW = labelFs * 1.6, ruleGap = labelFs * 0.9;
          var ruleH = Math.max(1, Math.round(labelFs * 0.05));
          var ruleY = labelY - labelFs * 0.30;
          g.globalAlpha = 0.65;
          g.fillRect(w / 2 - lw / 2 - ruleGap - ruleW, ruleY, ruleW, ruleH);
          g.fillRect(w / 2 + lw / 2 + ruleGap, ruleY, ruleW, ruleH);
          g.globalAlpha = 1;
        }
        /* big title */
        g.font = '800 ' + fs.toFixed(2) + 'px ' + FONT;
        for (j = 0; j < lines.length; j++) {
          fillTracked(g, lines[j], w / 2, firstBase + j * lineH, -0.025 * fs);
        }
        /* sub line */
        if (opts.subline) {
          g.globalAlpha = 0.85;
          g.font = '600 ' + subFs.toFixed(2) + 'px ' + FONT;
          fillTracked(g, opts.subline, w / 2, subY, 0.42 * subFs);
          g.globalAlpha = 1;
        }
      }

      /* soft halo passes give the alpha ramp the shader erodes against */
      g.shadowColor = 'rgba(255,255,255,0.85)';
      g.shadowBlur = fs * 0.12;
      drawAll();
      g.shadowBlur = fs * 0.045;
      drawAll();
      g.shadowColor = 'rgba(0,0,0,0)';
      g.shadowBlur = 0;
      drawAll();
      return c;
    }

    function uploadTextTexture() {
      if (!glOK || lost) return;
      var dpr = Math.min(window.devicePixelRatio || 1, opts.maxDPR);
      var ar = clamp(cssW / Math.max(1, cssH), 0.2, 5);
      var tw = Math.round(clamp(cssW * dpr, 640, 1600));
      var th = Math.round(tw / ar);
      if (th > 1800) { tw = Math.round(tw * 1800 / th); th = 1800; }
      var c = buildTextCanvas(Math.max(2, tw), Math.max(2, th));
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, gl.LUMINANCE, gl.UNSIGNED_BYTE, c);
    }

    /* ---------- sizing ---------- */
    function applyResolution() {
      var dpr = Math.min(window.devicePixelRatio || 1, opts.maxDPR);
      var s = LEVELS[level].res;
      var pw = Math.max(2, Math.round(cssW * dpr * s));
      var ph = Math.max(2, Math.round(cssH * dpr * s));
      if (pw * ph > MAX_RENDER_PIXELS) {
        var k = Math.sqrt(MAX_RENDER_PIXELS / (pw * ph));
        pw = Math.max(2, Math.round(pw * k));
        ph = Math.max(2, Math.round(ph * k));
      }
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
      }
    }

    function onResize() {
      var w = container.clientWidth, h = container.clientHeight;
      if (w < 2 || h < 2) return;
      if (w === cssW && h === cssH) return;
      cssW = w; cssH = h;
      aspect = cssW / cssH;
      applyResolution();
      window.clearTimeout(textTimer);
      textTimer = window.setTimeout(function () {
        if (destroyed) return;
        uploadTextTexture();
        if (!running) renderStill();
      }, 160);
      if (!running) renderStill();
    }

    /* ---------- render ---------- */
    function easeIntro() {
      var u = clamp(introT / introSec, 0, 1);
      return u * u * (3 - 2 * u);
    }

    function clarityNow() {
      var introText = sstep(0.35, 1.0, easeIntro());
      var cyc = 0.5 + 0.5 * Math.sin(simTime * cycleRate - 1.2);
      return introText * (0.30 + 0.70 * cyc);
    }

    function drawFrame() {
      if (!glOK || lost || destroyed) return;
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform2f(U.uRes, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1f(U.uTime, simTime);
      gl.uniform1f(U.uAspect, aspect);
      gl.uniform1f(U.uIntro, easeIntro());
      gl.uniform1f(U.uOctaves, LEVELS[level].oct);
      gl.uniform2f(U.uPointer, ptr.x, ptr.y);
      gl.uniform2f(U.uVel, vel.x, vel.y);
      gl.uniform1f(U.uEnergy, energy);
      gl.uniform2f(U.uBurstPos, bPos.x, bPos.y);
      gl.uniform1f(U.uBurstAge, burstAge);
      gl.uniform1f(U.uGrain, (simTime * 61.7) % 17.0 + 1.0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function renderStill() {
      /* used for reduced-motion + redraws while paused */
      if (reduced) { simTime = staticT; introT = introSec; }
      drawFrame();
    }

    function frame(now) {
      rafId = 0;
      if (!running) return;
      var dt = clamp((now - lastNow) / 1000, 0.0001, 0.05);
      lastNow = now;
      simTime += dt;
      if (simTime > 7200) simTime -= 7200; /* keep float precision healthy */
      if (introStarted && introT < introSec) introT = Math.min(introSec, introT + dt);

      /* pointer smoothing + velocity-scaled stir decay */
      var ke = 1 - Math.exp(-dt * 6.0);
      ptr.x += (ptrT.x - ptr.x) * ke;
      ptr.y += (ptrT.y - ptr.y) * ke;
      vel.x += (velT.x - vel.x) * ke;
      vel.y += (velT.y - vel.y) * ke;
      var dk = Math.exp(-dt * 2.2);
      velT.x *= dk; velT.y *= dk;
      stirT *= Math.exp(-dt * 2.4);                       /* smooth decay      */
      energy += (stirT - energy) * (1 - Math.exp(-dt * 7.0)); /* smooth follow */
      burstAge += dt;

      drawFrame();

      /* clarity sync event (throttled ~6/s) */
      if (now - lastClarityAt >= CLARITY_MS) {
        lastClarityAt = now;
        fireClarity(clarityNow());
      }

      /* rolling FPS -> adaptive quality ladder (down on strain, back up after
         sustained headroom; 10s cooldown + oscillation guard) */
      if (simTime > 1.5) {
        fpsAcc += dt; fpsN++;
        if (fpsN >= 50) {
          var avg = fpsN / fpsAcc;
          fpsAcc = 0; fpsN = 0;
          if (avg < DROP_FPS && level < LEVELS.length - 1) {
            /* dropped right after a raise: bar that tier so we never oscillate */
            if (now - lastRaise < OSC_WINDOW) raiseCeil = Math.max(raiseCeil, level + 1);
            level++;
            lastChange = now;
            applyResolution();
          } else if (avg > RAISE_FPS && level > startLevel && level > raiseCeil &&
                     now - lastChange > RAISE_COOLDOWN) {
            level--;
            lastRaise = now;
            lastChange = now;
            applyResolution();
          }
        }
      }
      rafId = requestAnimationFrame(frame);
    }

    function updateRunning() {
      var should = !destroyed && glOK && !lost && !reduced && pageVisible && inView && !userPaused;
      if (should && !running) {
        running = true;
        lastNow = performance.now();
        fpsAcc = 0; fpsN = 0;
        rafId = requestAnimationFrame(frame);
      } else if (!should && running) {
        running = false;
        if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      }
    }

    /* ---------- input (unified pointer: mouse + touch) ---------- */
    function pointerSpace(e) {
      var rect = container.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return null;
      return {
        x: ((e.clientX - rect.left) / rect.width - 0.5) * aspect,
        y: 0.5 - (e.clientY - rect.top) / rect.height
      };
    }

    function onPointerMove(e) {
      var s = pointerSpace(e);
      if (!s) return;
      var now = performance.now();
      if (lastMoveT > 0) {
        var dtv = Math.max(0.004, (now - lastMoveT) / 1000);
        var vx = (s.x - ptrT.x) / dtv;
        var vy = (s.y - ptrT.y) / dtv;
        var m = Math.sqrt(vx * vx + vy * vy);
        if (m > 2.5) { vx *= 2.5 / m; vy *= 2.5 / m; m = 2.5; }
        velT.x = velT.x * 0.4 + vx * 0.6;
        velT.y = velT.y * 0.4 + vy * 0.6;
        /* velocity-scaled advection: push ~ min(pointerSpeed * k, cap);
           slow hover stirs gently, fast swipes physically carve */
        var sT = Math.min(STIR_CAP, m * STIR_K);
        if (sT > stirT) stirT = sT;
      }
      ptrT.x = s.x; ptrT.y = s.y;
      lastMoveT = now;
    }

    function triggerBurst(ax, ay, strength) {
      var s = clamp(strength, 0, 1);
      bPos.x = ax; bPos.y = ay;
      burstAge = (1 - s) * 0.55;   /* weaker bursts start mid-envelope */
      stirT = Math.min(STIR_CAP, stirT + 0.45 * s);
      energy = Math.min(1, energy + 0.25 * s);
    }

    function onPointerDown(e) {
      var s = pointerSpace(e);
      if (!s) return;
      triggerBurst(s.x, s.y, 1);
      ptrT.x = s.x; ptrT.y = s.y;
    }

    /* ====================================================================
       BOOT — GL program first. NOTHING (listeners, observers, media query
       hooks) is attached until setupGL() succeeds, so the failure path can
       never leak handlers across init/destroy cycles.
       ==================================================================== */
    if (!setupGL()) return makeStub();

    /* ---------- wiring (post-success only) ---------- */
    if (opts.interactive) {
      container.addEventListener('pointermove', onPointerMove, { passive: true });
      container.addEventListener('pointerdown', onPointerDown, { passive: true });
      disposers.push(function () {
        container.removeEventListener('pointermove', onPointerMove);
        container.removeEventListener('pointerdown', onPointerDown);
      });
    }

    function onVisibility() { pageVisible = !document.hidden; updateRunning(); }
    document.addEventListener('visibilitychange', onVisibility, { passive: true });
    disposers.push(function () { document.removeEventListener('visibilitychange', onVisibility); });

    var io = null;
    if (hasIO) {
      inView = false;
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) inView = entries[i].isIntersecting;
        if (inView && !introStarted) introStarted = true; /* intro starts on first sight */
        updateRunning();
      }, { threshold: 0 });
      io.observe(container);
      disposers.push(function () { io.disconnect(); });
    }

    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(onResize);
      ro.observe(container);
      disposers.push(function () { ro.disconnect(); });
    } else {
      window.addEventListener('resize', onResize, { passive: true });
      disposers.push(function () { window.removeEventListener('resize', onResize); });
    }

    if (mq) {
      var onMQ = function () {
        reduced = !!mq.matches;
        updateRunning();
        if (reduced) { renderStill(); fireClarity(1); }
      };
      if (mq.addEventListener) { mq.addEventListener('change', onMQ); disposers.push(function () { mq.removeEventListener('change', onMQ); }); }
      else if (mq.addListener) { mq.addListener(onMQ); disposers.push(function () { mq.removeListener(onMQ); }); }
    }

    function onCtxLost(e) {
      e.preventDefault();
      lost = true;
      updateRunning();
      showFallback();
      fireClarity(1);
    }
    function onCtxRestored() {
      lost = false;
      if (setupGL()) {
        applyResolution();
        uploadTextTexture();
        hideFallback();
        if (reduced) { renderStill(); fireClarity(1); } else { updateRunning(); }
      } else {
        showFallback();
        fireClarity(1);
      }
    }
    canvas.addEventListener('webglcontextlost', onCtxLost, false);
    canvas.addEventListener('webglcontextrestored', onCtxRestored, false);
    disposers.push(function () {
      canvas.removeEventListener('webglcontextlost', onCtxLost);
      canvas.removeEventListener('webglcontextrestored', onCtxRestored);
    });

    /* ---------- first frame ---------- */
    applyResolution();
    uploadTextTexture();

    if (reduced) {
      renderStill();             /* one composed frame, no loop */
      window.setTimeout(function () { if (!destroyed) fireClarity(1); }, 0);
    } else {
      updateRunning();           /* IO will start the loop when visible */
    }

    /* ---------- public handle ---------- */
    var handle = {
      canvas: canvas,
      live: true,
      /* public shockwave: normalized coords (0..1, default center),
         optional strength 0..1 (default 1) */
      burst: function (nx, ny, strength) {
        if (destroyed) return;
        var px = +nx; if (!isFinite(px)) px = 0.5;
        var py = +ny; if (!isFinite(py)) py = 0.5;
        var s = +strength; if (!isFinite(s)) s = 1;
        triggerBurst((clamp(px, 0, 1) - 0.5) * aspect, 0.5 - clamp(py, 0, 1), s);
        if (!running && !reduced) renderStill();
      },
      pause: function () { userPaused = true; updateRunning(); },
      resume: function () { userPaused = false; updateRunning(); },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        running = false;
        if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
        window.clearTimeout(textTimer);
        for (var i = 0; i < disposers.length; i++) { try { disposers[i](); } catch (e) {} }
        disposers.length = 0;
        try {
          var ext = gl.getExtension('WEBGL_lose_context');
          if (ext) ext.loseContext();
        } catch (e) {}
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        if (fallback.parentNode) fallback.parentNode.removeChild(fallback);
        if (INSTANCES && INSTANCES.get(container) === handle) INSTANCES['delete'](container);
      }
    };

    if (INSTANCES) INSTANCES.set(container, handle);
    return handle;
  }

  window.initKhidyrSmoke = initKhidyrSmoke;
})();
